import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiClipboardTextOutline } from '@mdi/js';

/**
 * LifeProductSection Component
 * Life insurance product selection fields for the Product column
 */
const LifeProductSection = ({ formData, onInputChange, lifeProductOptions, activeProductTab, setActiveProductTab, jurisdictionOptions, confirmIfNeeded, styles }) => {
  const [showAllProducts, setShowAllProducts] = React.useState(false);

  // Get available product tabs based on lifeProductOptions
  const productTabs = [
    { id: 'TERM', label: 'TERM', products: lifeProductOptions.term_products || [] },
    { id: 'GUL', label: 'GUL', products: lifeProductOptions.guaranteed_life_products || [] },
    { id: 'IUL', label: 'IUL', products: lifeProductOptions.iul_products || [] },
    { id: 'ROP', label: 'ROP', products: lifeProductOptions.return_of_premium_products || [] },
    { id: 'OTHER', label: 'Other', products: lifeProductOptions.other_products || [] }
  ].filter(t => t.products.length > 0);

  const productLimit = lifeProductOptions.product_limits || 3;

  const handleProductSelection = product => {
    const current = formData.selectedProductTypes || [];
    if (current.includes(product)) {
      onInputChange(
        'selectedProductTypes',
        current.filter(p => p !== product)
      );
      return;
    }
    if (current.length < productLimit) {
      onInputChange('selectedProductTypes', [...current, product]);
    } else {
      alert(`You can select maximum ${productLimit} products`);
    }
  };

  // Get current tab's products
  const currentProducts = productTabs.find(t => t.id === activeProductTab)?.products || [];

  // Show first 5 products by default
  const displayedProducts = showAllProducts ? currentProducts : currentProducts.slice(0, 5);
  const hasMoreProducts = currentProducts.length > 5;

  return (
    <div style={styles.formGrid}>
      {/* Product Type Tabs */}
      <div style={styles.formGroup} data-itemid='product-type-wrapper'>
        <div style={styles.row} data-itemid='product-type-label-wrapper'>
          <label style={styles.label} data-itemid='product-type-label'>
            Product Type
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='product-type-label-required'>
            *
          </label>
        </div>
        <div style={styles.buttonGroup}>
          {productTabs.map(tab => (
            <button key={tab.id} onClick={() => {
              setActiveProductTab(tab.id);
              onInputChange('activeProductTab', tab.id);
            }} 
            style={styles.button(activeProductTab === tab.id)} type='button' data-itemid={`product-type-button-${tab.label}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Product Selection */}
      <div style={styles.formGroup} data-itemid='product-selection-wrapper'>
        <div style={styles.row} data-itemid='product-selection-label-wrapper'>
          <label style={styles.label} data-itemid='product-selection-label'>
            Select Products ({(formData.selectedProductTypes || []).length}/{productLimit} selected)
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='product-selection-label-required'>
            *
          </label>
        </div>
        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '12px',
            backgroundColor: '#fafafa',
            maxHeight: showAllProducts ? '300px' : 'auto',
            overflowY: showAllProducts ? 'auto' : 'visible'
          }}
          data-itemid='product-selection-options-wrapper'
        >
          {displayedProducts.length > 0 ? (
            <>
              {displayedProducts.map(p => (
                <div key={p} style={{ marginBottom: 8 }} data-itemid={`product-selection-option-wrapper-${p}`}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} data-itemid={`product-selection-option-label-${p}`}>
                    <input
                      checked={(formData.selectedProductTypes || []).includes(p)}
                      onChange={() => handleProductSelection(p)}
                      style={{ marginRight: 8 }}
                      type='checkbox'
                      data-itemid={`product-selection-option-field-${p}`}
                    />
                    <span style={{ fontSize: 13 }} data-itemid={`product-selection-option-text-${p}`}>
                      {p}
                    </span>
                  </label>
                </div>
              ))}

              {/* Show more/less button */}
              {hasMoreProducts && (
                <button
                  onClick={() => setShowAllProducts(!showAllProducts)}
                  style={{
                    marginTop: '8px',
                    background: 'none',
                    border: 'none',
                    color: '#017DB7',
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '4px 0',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  type='button'
                  data-itemid='product-selection-show-more-button'
                >
                  <Icon path={showAllProducts ? mdiChevronDown : mdiChevronRight} size={0.6} data-itemid='product-selection-show-more-button-icon' />
                  {showAllProducts ? 'Show less' : `Show ${currentProducts.length - 5} more options`}
                </button>
              )}
            </>
          ) : (
            <div style={{ color: '#6c757d', fontStyle: 'italic', fontSize: 13 }} data-itemid='product-selection-no-products'>
              No products available in this category
            </div>
          )}
        </div>
      </div>

      {/* Jurisdiction */}
      <div style={styles.formGroup} data-itemid='jurisdiction-wrapper'>
        <div style={styles.row} data-itemid='jurisdiction-label-wrapper'>
          <label style={styles.label} data-itemid='jurisdiction-label'>
            Jurisdiction
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='jurisdiction-label-required'>
            *
          </label>
        </div>
        <select onChange={e => onInputChange('jurisdictionLife', e.target.value)} style={styles.select} value={formData.jurisdictionLife || ''} data-itemid='jurisdiction-select'>
          <option value='' data-itemid='jurisdiction-option-empty'>
            Select State
          </option>
          {jurisdictionOptions.map(state => (
            <option data-itemid={`jurisdiction-option-${state.value}`} key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
      </div>

      {/* Carriers */}
      <div style={styles.formGroup} data-itemid='carriers-wrapper'>
        <div style={styles.row} data-itemid='carriers-label-wrapper'>
          <label style={styles.label} data-itemid='carriers-label'>
            Carriers
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='carriers-label-required'>
            *
          </label>
        </div>
        <button
          onClick={() => confirmIfNeeded('select-carriers')}
          style={{ ...styles.carriersButton, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          type='button'
          data-itemid='carriers-button'
        >
          <Icon path={mdiClipboardTextOutline} size={0.7} data-itemid='carriers-button-icon' /> Select Carriers ({(formData.selectedCarriers || []).length} Selected)
        </button>
      </div>
    </div>
  );
};

LifeProductSection.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  lifeProductOptions: PropTypes.object.isRequired,
  activeProductTab: PropTypes.string.isRequired,
  setActiveProductTab: PropTypes.func.isRequired,
  jurisdictionOptions: PropTypes.array.isRequired,
  confirmIfNeeded: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired
};

export default LifeProductSection;
